import {
  FINANCE_SORTS,
  formatDate,
  OVERVIEW_DEFAULT_STATUSES,
  OVERVIEW_SORTS,
  overviewPeriodSchema,
  type OverviewRow,
  type OverviewSort,
  type PreferenceValue,
  ProjectStatuses,
  routes,
  SORT_ORDERS,
  type SortOrder,
  todayIso,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  CheckboxGroup,
  EmptyState,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { IndicatorStrip } from '../../entities/projects/indicator-strip';
import { PriorityBadge, ProjectStatusBadge } from '../../entities/projects/project-badges';
import { useApiQuery } from '../../shared/api/hooks';
import { usePermissions } from '../../shared/auth/session';
import { useEnumLabel } from '../../shared/i18n';
import { usePreference } from '../../shared/preferences';

type Filters = PreferenceValue<'overviewFilters'>;

/** This year, every status but Cancelled, name A→Z (FR-OVR-002). */
function defaults(): Filters {
  const year = todayIso().slice(0, 4);
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
    statuses: [...OVERVIEW_DEFAULT_STATUSES],
    sort: 'name',
    order: 'asc',
  };
}

/** Pārskats (FR-OVR-001…007): projects started in a period with their indicator strips. */
export function OverviewPage() {
  const { t } = useTranslation();
  const pref = usePreference('overviewFilters');
  return (
    <>
      <PageHeader title={t('overview.title')} />
      {pref.value === undefined ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <Overview filters={pref.value ?? defaults()} save={pref.save} />
      )}
    </>
  );
}

function Overview({ filters, save }: { filters: Filters; save: (next: Filters) => void }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const finance = usePermissions().includes('finance.view');
  // Dates take effect on "Meklēt" / Enter; sort and statuses at once — always with valid dates.
  const [period, setPeriod] = useState({ from: filters.from, to: filters.to });
  const [error, setError] = useState<string | null>(null);
  const apply = (change: Partial<Filters> = {}) => {
    const checked = overviewPeriodSchema.safeParse(period);
    setError(checked.success ? null : (checked.error.issues[0]?.message ?? null));
    if (checked.success) save({ ...filters, ...period, ...change });
  };

  const overview = useApiQuery(
    routes.overview.get,
    {
      query: {
        from: filters.from,
        to: filters.to,
        statuses: filters.statuses.join(','),
        sort: filters.sort,
        order: filters.order,
      },
    },
    { placeholderData: (previous) => previous },
  );
  const sorts = OVERVIEW_SORTS.filter((s) => finance || !FINANCE_SORTS.includes(s)).flatMap((sort) =>
    SORT_ORDERS.map((order) => ({ value: `${sort}:${order}`, label: t(`overview.sorts.${sort}_${order}`) })),
  );
  const rows = overview.data?.rows;

  return (
    <div className="flex flex-col gap-5">
      <form
        className="flex flex-col gap-4 rounded-card border border-border bg-surface p-4"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <div className="flex flex-wrap items-end gap-4">
          <TextField
            label={t('overview.from')}
            type="date"
            value={period.from}
            onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
          />
          <TextField
            label={t('overview.to')}
            type="date"
            value={period.to}
            onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
          />
          <div className="w-72">
            <SelectField
              label={t('overview.sort')}
              options={sorts}
              value={`${filters.sort}:${filters.order}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split(':') as [OverviewSort, SortOrder];
                apply({ sort, order });
              }}
            />
          </div>
          <Button type="submit" icon="search">
            {t('common.search')}
          </Button>
        </div>
        <CheckboxGroup
          label={t('overview.statuses')}
          options={ProjectStatuses.codes.map((code) => ({
            value: code,
            label: label(ProjectStatuses.byCode(code)),
          }))}
          value={filters.statuses}
          onChange={(statuses) => apply({ statuses })}
        />
        {error && <Alert tone="danger">{error}</Alert>}
      </form>

      {rows === undefined ? (
        <Spinner label={t('common.loading')} />
      ) : rows.length === 0 ? (
        <EmptyState icon="search" title={t('overview.empty')} />
      ) : (
        <>
          <p className="text-sm text-ink-muted" data-testid="overview-count">
            {t('overview.found', { count: rows.length })}
          </p>
          <ul aria-label={t('overview.title')} className="flex flex-col gap-4">
            {rows.map((r) => (
              <OverviewItem key={r.id} row={r} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Name, badges, "customer · start – finish" and the indicator strip (FR-OVR-005). */
function OverviewItem({ row: r }: { row: OverviewRow }) {
  return (
    <li aria-label={r.name} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/projects/${r.id}`} className="text-base font-semibold text-brand-700 hover:underline">
          {r.name}
        </Link>
        <ProjectStatusBadge status={r.status} />
        <PriorityBadge priority={r.priority} />
        <span className="text-sm text-ink-muted">
          {r.customerName} · {formatDate(r.startDate)} – {formatDate(r.plannedFinish)}
        </span>
      </div>
      <IndicatorStrip indicators={r.indicators} financialManagement={r.financialManagement} />
    </li>
  );
}
